import { useCallback, useState } from 'react';
import { useAuth } from '../onboarding/AuthContext';
import { ApiProblemError, createFrenetDeposit, getLatestFrenetDeposit, type FrenetDeposit } from '../../lib/api-client';

export interface UseFrenetDepositResult {
  /** Most recent deposit attempt for this org, or `null` if never attempted / not loaded yet. */
  latest: FrenetDeposit | null;
  loadingLatest: boolean;
  /** `true` while `POST /providers/frenet/deposit` is in flight. */
  starting: boolean;
  error: string | null;
  /** Preference id from the most recent successful `start()` call — `FrenetDepositSection.tsx` redirects the whole page to Mercado Pago's checkout once this is set (via `mercadoPagoCheckout.ts#getMercadoPagoCheckoutRedirectUrl`, called from THAT component, not this hook — this hook never touches Mercado Pago at all). The redirect navigates away, so nothing in this hook ever needs to reset it back to `null` — a fresh page load after the checkout return (`?deposit=`, see `ProvidersPage.tsx`) gets a fresh hook instance. */
  preferenceId: string | null;
  /** Re-fetches `GET /internal/providers/frenet/deposit/latest` — called on mount and after returning from the Mercado Pago checkout (`?deposit=` query param, see `ProvidersPage.tsx`). */
  refreshLatest: () => Promise<void>;
  /** Starts a new deposit for `valueCents` — sets `preferenceId` on success so the caller can redirect to checkout. */
  start: (valueCents: number) => Promise<void>;
}

/**
 * `POST /internal/providers/frenet/deposit` + `GET .../deposit/latest`
 * action/state (TTP-270) — a separate hook (not inlined in
 * `ProviderConnectCard.tsx`/`ProvidersPage.tsx`), same posture
 * `features/billing/useSubscribeTier.ts` established for its own
 * Mercado-Pago flow. Deliberately never imports `mercadoPagoCheckout.ts` —
 * the actual `window.location.href` redirect lives in
 * `FrenetDepositSection.tsx`'s own `useEffect` on `preferenceId`, not here.
 */
export function useFrenetDeposit(): UseFrenetDepositResult {
  const { user } = useAuth();
  const [latest, setLatest] = useState<FrenetDeposit | null>(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferenceId, setPreferenceId] = useState<string | null>(null);

  const refreshLatest = useCallback(async () => {
    if (!user) return;
    setLoadingLatest(true);
    try {
      const idToken = await user.getIdToken();
      const { deposit } = await getLatestFrenetDeposit(idToken);
      setLatest(deposit);
    } catch {
      // Best-effort — same posture as ProvidersPage.tsx's own balance load: a failure here never blocks the rest of the screen.
    } finally {
      setLoadingLatest(false);
    }
  }, [user]);

  async function start(valueCents: number) {
    if (!user) return;
    setStarting(true);
    setError(null);
    setPreferenceId(null);
    try {
      const idToken = await user.getIdToken();
      const result = await createFrenetDeposit(idToken, valueCents);
      setPreferenceId(result.preference_id);
    } catch (err) {
      setError(
        err instanceof ApiProblemError
          ? (err.problem.detail ?? err.problem.title)
          : 'Não foi possível iniciar o depósito agora.',
      );
    } finally {
      setStarting(false);
    }
  }

  return {
    latest,
    loadingLatest,
    starting,
    error,
    preferenceId,
    refreshLatest,
    start,
  };
}
